import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sparkles, LayoutDashboard, FileText, LayoutGrid,
  Images, CalendarRange, CalendarDays,
  User, LogOut, Plug, HelpCircle, CreditCard,
} from 'lucide-react';
import { useAuth } from 'components/context/AuthContext';
import { useHelpCenter } from 'components/context/HelpCenterContext';
import { ModalsContext } from 'components/context/ModalsContext';
import './AdminDock.css';

// Destinos principales del dock (los más usados). El resto va en "Otras
// herramientas" (flyout) para mantener el dock corto y legible, como el del GP.
const PRIMARY = [
  { icon: Sparkles, label: 'Bebo IA', path: '/admin/bebo' },
  { icon: LayoutDashboard, label: 'Panel', path: '/admin/panel' },
  { icon: FileText, label: 'Reportes', path: '/admin/reportes' },
];

// "Otras herramientas": el resto de las secciones del admin, agrupadas en un
// menú flotante que se abre al costado del dock.
const MORE = [
  { icon: Images, label: 'Biblioteca', path: '/admin/creative-library' },
  { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
  { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
];

/**
 * Dock de navegación vertical del admin: pastilla glass flotante a la izquierda
 * (calcada del dock del GP), con los destinos principales + un flyout "Otras
 * herramientas" para el resto, y abajo la cuenta (perfil, conexiones, soporte,
 * suscripción y cerrar sesión) — igual que el GP tiene el logout en el dock.
 * La navegación la resuelve el padre (`onNavigate`).
 */
export default function AdminDock({ onNavigate, dark = false }) {
  const location = useLocation();
  const { auth, logout } = useAuth();
  const { openHelpCenter } = useHelpCenter();
  const { openPlansModal, openConnectionsModal } = useContext(ModalsContext);
  const [moreOpen, setMoreOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const moreRef = useRef(null);
  const acctRef = useRef(null);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const moreActive = MORE.some((i) => isActive(i.path));

  // Cerrar los flyouts al hacer click afuera o con Escape.
  useEffect(() => {
    if (!moreOpen && !acctOpen) return undefined;
    const onDown = (e) => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (acctOpen && acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setMoreOpen(false); setAcctOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen, acctOpen]);

  // Cerrar los flyouts cuando cambia la ruta.
  useEffect(() => { setMoreOpen(false); setAcctOpen(false); }, [location.pathname]);

  const go = (path) => { setMoreOpen(false); setAcctOpen(false); onNavigate(path); };

  const initial = auth?.user?.first_name?.[0]?.toUpperCase() || 'U';
  const displayName = auth?.user?.name || auth?.user?.first_name || 'Cuenta';

  return (
    <nav className={`admdock ${dark ? 'admdock--dark' : ''}`} aria-label="Navegación del administrador">
      <div className="admdock-inner">
        {PRIMARY.map(({ icon: Icon, label, path }) => (
          <button
            key={path}
            type="button"
            className={`admdock-item ${isActive(path) ? 'active' : ''}`}
            onClick={() => go(path)}
            aria-label={label}
            aria-current={isActive(path) ? 'page' : undefined}
          >
            <Icon size={21} strokeWidth={1.8} aria-hidden="true" />
            <span className="admdock-tip">{label}</span>
          </button>
        ))}

        <div className="admdock-more" ref={moreRef}>
          <button
            type="button"
            className={`admdock-item ${moreActive ? 'active' : ''} ${moreOpen ? 'is-open' : ''}`}
            onClick={() => { setAcctOpen(false); setMoreOpen((v) => !v); }}
            aria-label="Otras herramientas"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
          >
            <LayoutGrid size={21} strokeWidth={1.8} aria-hidden="true" />
            {!moreOpen && <span className="admdock-tip">Otras herramientas</span>}
          </button>

          {moreOpen && (
            <div className="admdock-flyout" role="menu" aria-label="Otras herramientas">
              <span className="admdock-flyout-title">Otras herramientas</span>
              {MORE.map(({ icon: Icon, label, path }) => (
                <button
                  key={path}
                  type="button"
                  role="menuitem"
                  className={`admdock-flyout-item ${isActive(path) ? 'active' : ''}`}
                  onClick={() => go(path)}
                >
                  <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="admdock-sep" />

        <div className="admdock-more" ref={acctRef}>
          <button
            type="button"
            className={`admdock-item admdock-acct ${acctOpen ? 'is-open' : ''}`}
            onClick={() => { setMoreOpen(false); setAcctOpen((v) => !v); }}
            aria-label="Cuenta"
            aria-haspopup="menu"
            aria-expanded={acctOpen}
          >
            <span className="admdock-avatar">
              {auth?.user?.profile_image ? <img src={auth.user.profile_image} alt="" /> : initial}
            </span>
            {!acctOpen && <span className="admdock-tip">Cuenta</span>}
          </button>

          {acctOpen && (
            <div className="admdock-flyout" role="menu" aria-label="Cuenta">
              <div className="admdock-acct-head">
                <span className="admdock-avatar admdock-avatar-lg">
                  {auth?.user?.profile_image ? <img src={auth.user.profile_image} alt="" /> : initial}
                </span>
                <span className="admdock-acct-name">{displayName}</span>
              </div>
              <button type="button" role="menuitem" className="admdock-flyout-item" onClick={() => go('/profile')}>
                <User size={18} strokeWidth={1.8} aria-hidden="true" /> <span>Mi perfil</span>
              </button>
              <button type="button" role="menuitem" className="admdock-flyout-item" onClick={() => { setAcctOpen(false); openConnectionsModal?.(); }}>
                <Plug size={18} strokeWidth={1.8} aria-hidden="true" /> <span>Conexiones</span>
              </button>
              <button type="button" role="menuitem" className="admdock-flyout-item" onClick={() => { setAcctOpen(false); openHelpCenter?.(); }}>
                <HelpCircle size={18} strokeWidth={1.8} aria-hidden="true" /> <span>Soporte</span>
              </button>
              <button type="button" role="menuitem" className="admdock-flyout-item" onClick={() => { setAcctOpen(false); openPlansModal?.(); }}>
                <CreditCard size={18} strokeWidth={1.8} aria-hidden="true" /> <span>Suscripción</span>
              </button>
              <div className="admdock-flyout-sep" />
              <button type="button" role="menuitem" className="admdock-flyout-item" onClick={logout}>
                <LogOut size={18} strokeWidth={1.8} aria-hidden="true" /> <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
